import React, { useState } from "react";
import { useParams } from "react-router-dom";
import InfluencersApi from "~/admin/apis/influencers.api";
import Card from "~/admin/components/Card";
import ConfirmationButton from "~/admin/components/ConfirmationButton";
import PageDataLoader from "~/admin/components/PageDataLoader";
import TabBar from "~/admin/components/TabBar";
import useDataLoading from "~/admin/hooks/useDataLoading";
import useLayout from "~/hooks/useLayout";
import useLoader from "~/hooks/useLoader";
import useMount from "~/hooks/useMount";
import Notifications from "~/admin/services/Notifications";
import DocumentsTab from "~/admin/components/DocumentsTab";
import InformationsTab from "./InformationsTab";
import useDemands from "~/admin/hooks/useDemandsContext";

export default function InfluencerPage() {
  const {
    demands: { demands },
  } = useDemands();

  const { setDemands } = useDemands();
  const params = useParams();
  const { setLayout } = useLayout();
  const [activeTab, setActiveTab] = useState("informations");
  const [influencer, setInfluencer] = useState(null);
  const [canValidate, setCanValidate] = useState(false);
  const dataLoading = useDataLoading();
  const loader = useLoader();

  const tabItems = [
    {
      key: "informations",
      title: "Informations",
    },
    {
      key: "documents",
      title: "Documents",
    },
  ];

  useMount(() => {
    loadData();
  });

  const loadData = async () => {
    dataLoading.start();
    setInfluencer(null);

    let error = null;
    const influencer = await InfluencersApi.get(params.influencerId).catch(
      (e) => {
        error = e;
        return null;
      }
    );

    dataLoading.stop(error);

    if (influencer) {
      setInfluencer(influencer);

      if (!influencer.user.documents.find((document) => !document.isValid)) {
        setCanValidate(true);
      }

      if (influencer.user.checkStatus !== "valid") {
        setLayout({
          title: "Demandes",
          activeMenuKeys: ["demands"],
        });
        setActiveTab("documents");
      } else {
        setLayout({
          title: "Utilisateurs",
          activeMenuKeys: ["users"],
        });
      }
    }
  };

  const changeTab = (key) => {
    setActiveTab(key);
  };

  const validateUser = async (influencerId) => {
    loader.show();
    await InfluencersApi.validate(influencerId).catch((e) => {
      Notifications.error("Erreur lors de la validation de l'utilisateur");
      return null;
    });

    setDemands({
      demands: demands - 1,
    });

    Notifications.success("Utilisateur validé avec succès");
    loader.hide();
    loadData();
  };

  return (
    <PageDataLoader isLoading={dataLoading.isLoading}>
      <>
        <div className="w-full h-[60px] border-b-2 border-slate-700 flex justify-end items-center px-10">
          {influencer && (
            <>
              {influencer.user.checkStatus !== "valid" && canValidate && (
                <ConfirmationButton
                  confirmationTitle="Valider l'utilisateur"
                  confirmationBody={`Êtes-vous sur de vouloir valider l'utilisateur ${influencer.lastName} ${influencer.firstName}`}
                  data={influencer.id}
                  onConfirm={validateUser}
                >
                  Valider l'utilisateur
                </ConfirmationButton>
              )}

              <div className="font-bold text-2xl ml-4">
                {influencer.lastName} {influencer.firstName}
              </div>
            </>
          )}
        </div>
        <div className="p-6 overflow-auto">
          <Card>
            <TabBar
              items={tabItems}
              activeItem={activeTab}
              onChange={changeTab}
            />
            <div className="pt-4">
              {influencer && activeTab === "informations" && (
                <InformationsTab influencer={influencer} />
              )}
              {influencer && activeTab === "documents" && (
                <DocumentsTab user={influencer} reload={loadData} />
              )}
            </div>
          </Card>
        </div>
      </>
    </PageDataLoader>
  );
}
